import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ENDPOINTS } from "@/common/utils/url-registry";
import { BRIDGE_REQUIRED_PARAMS } from "@/common/utils/bridge-constants";
import { useDelayedFlag } from "@/state/hooks/useDelayedFlag";

const REQUIRED_PARAMS = BRIDGE_REQUIRED_PARAMS;

/**
 * EN-1053: how long to wait for the /login hand-off before assuming it's stuck.
 * Generous on purpose — this only fires while the page is still mounted (i.e. the
 * browser never actually left), so a slow-but-succeeding navigation never sees it.
 */
const REDIRECT_TIMEOUT_MS = 6000;

/**
 * Bridge OAuth gate (unauthenticated).
 * When the desktop app (bridge) sends the user here with PKCE params,
 * we redirect to the normal login and preserve a returnTo so that after
 * login the user lands back on /bridge-auth with the same params, at which
 * point the authenticated route will exchange for a one-time code and redirect.
 *
 * This page is a pure redirector — it never renders content on the happy path.
 * The required PKCE params are an internal protocol detail; surfacing them as
 * on-screen text would leak developer-facing copy to end users. That happened
 * during logout: the auth→unauthenticated router swap briefly mounts this route
 * with no params, which used to flash a "Missing required parameters:
 * redirect_uri…" message. When params are absent we just send the user home instead.
 *
 * EN-1053: a thrown error or a browser silently blocking the navigation used to
 * leave this page blank forever with nothing to report. Both are now caught and
 * surfaced as a generic, actionable message that never repeats the PKCE params.
 */
export default function BridgeAuthPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [threwError, setThrewError] = useState(false);
    const [attempting, setAttempting] = useState(false);
    const timedOut = useDelayedFlag(attempting, REDIRECT_TIMEOUT_MS);

    useEffect(() => {
        const missing = REQUIRED_PARAMS.filter((key) => !searchParams.get(key));
        if (missing.length > 0) {
            navigate("/", { replace: true });
            return;
        }

        setAttempting(true);
        try {
            const returnTo = `/${ENDPOINTS.BridgeAuth}?${searchParams.toString()}`;
            window.location.href = `/${ENDPOINTS.Login}?returnTo=${encodeURIComponent(returnTo)}`;
        } catch {
            setThrewError(true);
        }
    }, [searchParams, navigate]);

    if (threwError || timedOut) {
        return (
            <div className="flex min-h-screen flex-1 flex-col items-center justify-center px-6 py-12">
                <p className="text-center text-gray-900">Something went wrong starting sign-in.</p>
                <button type="button" className="mt-4 text-teal-600 underline" onClick={() => window.location.reload()}>
                    Try again
                </button>
            </div>
        );
    }

    return null; // still redirecting; never render protocol details to the user
}
